import React from 'react';
import { Grid, Group, NumberInput, Select, Text, TextInput } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import type { IEquipoEscalera } from '../../../../modelos/CotizadorNBS/entidades/IEquipoEscalera';
import type { IOpcionesEquiposNBS } from '../../../../modelos/CotizadorNBS';
import { conValorActual } from '../../utils/opcionesEquipos';
import type { ErroresFormulario } from '../../utils/camposEspecificacion';

interface DatosEscaleraVistaProps {
    form: Partial<IEquipoEscalera>;
    alCambiar: (cambios: Partial<IEquipoEscalera>) => void;
    opciones: IOpcionesEquiposNBS | null;
    errores: ErroresFormulario;
    deshabilitado: boolean;
}

const unidad = (texto: string) => <Text size="xs" c="dimmed" mr="xs" style={{ pointerEvents: 'none' }}>{texto}</Text>;
/** Unidad junto a la flecha del select (la flecha se reemplaza al poner rightSection). */
const unidadSelect = (texto: string) => (
    <Group gap={2} wrap="nowrap" mr="xs" style={{ pointerEvents: 'none' }}>
        <Text size="xs" c="dimmed">{texto}</Text><IconChevronDown size={14} color="var(--mantine-color-gray-6)" />
    </Group>
);
const sinOpciones = 'Sin opciones disponibles';
const columna = { base: 12, xs: 6, md: 4 };

/** Pestaña "Datos del equipo" de la escalera o rampa: lo que usa el cálculo. */
export const DatosEscaleraVista: React.FC<DatosEscaleraVistaProps> = ({ form, alCambiar, opciones, errores, deshabilitado }) => (
    <Grid>
        <Grid.Col span={columna}>
            <TextInput size="xs" label="Nombre" error={errores.Nombre} withAsterisk value={form.Nombre || ''}
                onChange={(e) => alCambiar({ Nombre: e.currentTarget.value })} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <Select size="xs" label="Tipo" error={errores.Tipo} withAsterisk searchable nothingFoundMessage={sinOpciones}
                data={conValorActual(opciones?.escaleras.tipos, form.Tipo)} value={form.Tipo || null}
                onChange={(v) => alCambiar({ Tipo: v || '' })} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <Select size="xs" label="Modelo" error={errores.Producto} withAsterisk searchable nothingFoundMessage={sinOpciones}
                data={conValorActual(opciones?.escaleras.productos, form.Producto, form.ProductoNombre)}
                value={form.Producto || null} onChange={(v) => alCambiar({ Producto: v || '' })} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <NumberInput size="xs" label="Contenedores" error={errores.Contenedores} withAsterisk min={0} allowNegative={false} value={form.Contenedores ?? ''}
                onChange={(v) => alCambiar({ Contenedores: v === '' ? undefined : Number(v) })} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <Select size="xs" label="Velocidad" error={errores.VelocidadIngreso} withAsterisk searchable nothingFoundMessage={sinOpciones}
                data={opciones?.escaleras.velocidades || []} value={form.VelocidadIngreso ? String(form.VelocidadIngreso) : null}
                onChange={(v) => alCambiar({ VelocidadIngreso: Number(v) || 0 })}
                rightSection={unidadSelect('m/s')} rightSectionWidth={50} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <NumberInput size="xs" label="Altura" error={errores.Altura} withAsterisk min={0} allowNegative={false} allowDecimal={false}
                value={form.Altura || ''} onChange={(v) => alCambiar({ Altura: Number(v) })}
                rightSection={unidad('mm')} rightSectionWidth={35} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <Select size="xs" label="Ancho de pasos" error={errores.AnchoPasos} withAsterisk searchable nothingFoundMessage={sinOpciones}
                data={opciones?.escaleras.anchosPasos || []} value={form.AnchoPasos ? String(form.AnchoPasos) : null}
                onChange={(v) => alCambiar({ AnchoPasos: Number(v) || 0 })}
                rightSection={unidadSelect('mm')} rightSectionWidth={50} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <Select size="xs" label="Inclinación" error={errores.Inclinacion} withAsterisk searchable nothingFoundMessage={sinOpciones}
                data={opciones?.escaleras.inclinaciones || []} value={form.Inclinacion ? String(form.Inclinacion) : null}
                onChange={(v) => alCambiar({ Inclinacion: Number(v) || 0 })}
                rightSection={unidadSelect('°')} rightSectionWidth={40} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <NumberInput size="xs" label="Potencia Motor" error={errores.PotenciaMotor} withAsterisk min={0} allowNegative={false}
                value={form.PotenciaMotor || ''} onChange={(v) => alCambiar({ PotenciaMotor: Number(v) })}
                rightSection={unidad('kW')} rightSectionWidth={35} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <NumberInput size="xs" label="Cantidad" error={errores.Cantidad} withAsterisk min={1} allowNegative={false} allowDecimal={false}
                value={form.Cantidad || ''} onChange={(v) => alCambiar({ Cantidad: Number(v) })} disabled={deshabilitado} />
        </Grid.Col>
        <Grid.Col span={columna}>
            <NumberInput size="xs" label="Costo Equipo" error={errores.CostoFCA} withAsterisk min={0} allowNegative={false} thousandSeparator
                value={form.CostoFCA ?? ''} onChange={(v) => alCambiar({ CostoFCA: v === '' ? undefined : Number(v) })}
                leftSection={<Text size="xs" c="dimmed">$</Text>} rightSection={unidad('USD')} rightSectionWidth={40}
                disabled={deshabilitado} />
        </Grid.Col>
    </Grid>
);
